import blinkLaunchSlide01 from "@assets/splash-screens/blink-launch/slide-01.webp";
import blinkLaunchSlide02 from "@assets/splash-screens/blink-launch/slide-02.webp";
import blinkLaunchSlide03 from "@assets/splash-screens/blink-launch/slide-03.webp";
import floorPlanGenerationSlide01 from "@assets/splash-screens/floor-plan-generation/slide-01.webp";
import floorPlanGenerationSlide02 from "@assets/splash-screens/floor-plan-generation/slide-02.webp";
import import3dModelSlide01 from "@assets/splash-screens/import-3d-model/slide-01.webp";
import import3dModelSlide02 from "@assets/splash-screens/import-3d-model/slide-02.gif";
import welcomeToFaroSlide02 from "@assets/splash-screens/welcome-to-faro/slide-02.webp";
import welcomeToFaroSlide03 from "@assets/splash-screens/welcome-to-faro/slide-03.webp";
import welcomeToFaroSlide04 from "@assets/splash-screens/welcome-to-faro/slide-04.webp";
import welcomeToFaroSlide05 from "@assets/splash-screens/welcome-to-faro/slide-05.webp";
import welcomeToFaroSlide06 from "@assets/splash-screens/welcome-to-faro/slide-06.webp";
import accProcoreIntegrationSlide01 from "@assets/splash-screens/acc-procore-integration/slide-01.webp";
import accProcoreIntegrationSlide02 from "@assets/splash-screens/acc-procore-integration/slide-02.webp";
import droneDataSlide01 from "@assets/splash-screens/drone-data/slide-01.png";
import droneDataSlide02 from "@assets/splash-screens/drone-data/slide-02.png";
import droneDataSlide03 from "@assets/splash-screens/drone-data/slide-03.png";
import { ExternalLinks } from "@components/external-links";
import { FaroCoreApiConst } from "@stellar/core-api-client";

/**
 * All the possible keys for SDB Splash screen
 * The values are going to be used to pass to backend and
 * make it clear that they should be shown in Sphere Dashboard and not HoloBuilder apps.
 * Important: Don't change the values, as they are used to pass to backend and check if user has seen the splash screen
 * Important: Don't change the orders of the values, as they are used to determine the order of the splash screens
 */
export let SplashScreenSdbTopic = /*#__PURE__*/function (SplashScreenSdbTopic) {
  /**
   * Onboarding to Sphere XG for users migrated from HoloBuilder subscriptions.
   * Keep this topic on top of the list to ensure it has the highest priority among splash screens.
   * The prefix "sxg" is used to indicate that this is a generic Sphere XG splash screen, not tied to
   * the Dashboard app. Other apps like the Viewer will display the same screen and use the same id.
   */
  SplashScreenSdbTopic["hbToSxgMigrated"] = "sxg:hb-to-sxg-migrated";
  /** Showing welcome page and introducing the new functionalities */
  SplashScreenSdbTopic["welcomeToFaro"] = "sdb:welcome-to-faro";
  /** Showing tutorial for importing 3d models */
  SplashScreenSdbTopic["import3DModels"] = "sdb:import-3d-models";
  /** Showing blink launch */
  SplashScreenSdbTopic["blinkLaunch"] = "sdb:blink-launch";
  /** Promote Floor Plan Generation */
  SplashScreenSdbTopic["floorPlanGeneration"] = "sdb:floor-plan-generation";
  /** Showing ACC and Procore integration */
  SplashScreenSdbTopic["accProcoreIntegration"] = "sdb:acc-procore-integration";
  /** Showing drone data */
  SplashScreenSdbTopic["droneData"] = "sdb:drone-data";
  return SplashScreenSdbTopic;
}({});
const welcomeToFaroScreens = {
  topic: SplashScreenSdbTopic.welcomeToFaro,
  allowedRoles: FaroCoreApiConst.allCompanyRoles,
  disallowedFeatures: ["sphere-dashboard"],
  // Do not show this splash screen to Sphere XG exclusive companies
  // shouldExclude: (company) => company?.isSphereLegacyMigrated ?? false,
  // Do not show this splash screen for now in order to promote the blink release
  shouldExclude: () => true,
  content: [{
    title: "New & Intuitive design",
    description: `Find your data in a fresh and modern design in Sphere XG. 
        The intuitive interface makes managing your projects efficient and enjoyable.`,
    image: {
      src: welcomeToFaroSlide02,
      altText: "New dashboard design"
    }
  }, {
    title: "Boost efficiency with bulk actions",
    description: `Bulk actions help you save time when making changes across projects, 
        team members or groups at scale. Select multiple members, projects or groups to perform 
        bulk actions such as sending emails, changing roles, archiving, and more.`,
    image: {
      src: welcomeToFaroSlide03,
      altText: "Bulk actions"
    }
  }, {
    title: "Know exactly what’s happening",
    description: `Keep the track of your reality capture data. 
          See the processing status for VideoMode and point cloud processing in real-time.`,
    image: {
      src: welcomeToFaroSlide04,
      altText: "Updated project status"
    }
  }, {
    title: "Keep track of issues across your project",
    description: `360 Markups are now called annotations. Have a complete overview of all issues at a glance, 
        keep track of their status and access them for review.`,
    image: {
      src: welcomeToFaroSlide05,
      altText: "Site annotations"
    }
  }, {
    title: "Try the new dashboard for yourself",
    description: `Now it’s time to experience the new dashboard. If you have questions or suggestions, 
        please use the feedback button and let us know. We're excited to hear from you!`,
    image: {
      src: welcomeToFaroSlide06,
      altText: "Give us feedback"
    }
  }]
};
const import3DModelsScreens = {
  topic: SplashScreenSdbTopic.import3DModels,
  // All users should see this splash screen
  allowedRoles: FaroCoreApiConst.allCompanyRoles,
  // Do not show this splash screen for now in order to promote the blink release
  shouldExclude: () => true,
  content: [{
    title: "Bring Your 3D Models and BIM Data Into FARO Sphere® XG!",
    description: `Import your 3D models into Sphere XG to view and manage all your reality capture 
        and 3D design data efficiently within one unified digital reality platform.`,
    image: {
      src: import3dModelSlide01,
      altText: "Importing 3D models to Dashboard"
    }
  }, {
    title: "Find Discrepancies Early On With Accuracy",
    description: `The new overlay view of your 3D models and point clouds allows you to compare site 
        conditions with the plan, spot issues early, and visually inspect for future clashes to prevent rework.`,
    image: {
      src: import3dModelSlide02,
      altText: "Finding discrepancies"
    }
  }]
};
const blinkLaunchScreens = {
  topic: SplashScreenSdbTopic.blinkLaunch,
  // All users should see this splash screen
  allowedRoles: FaroCoreApiConst.allCompanyRoles,
  content: [{
    title: "From site to insights in a blink",
    description: <>
          Meet the new FARO
          <span style={{
        fontSize: "0.75rem",
        verticalAlign: "top"
      }}>
            ®
          </span>{" "}
          Blink™ Imaging Laser Scanner — your approachable reality capture
          solution. Blink combines stunning visual clarity with accurate point
          cloud data to deliver expert-level results with minimal training.
        </>,
    image: {
      src: blinkLaunchSlide01,
      altText: "New Blink launch"
    }
  }, {
    title: "Scanning for everyone",
    description: `With intuitive guided workflows and automated point cloud registration, 
      Blink simplifies high-quality scanning for designers, builders, surveyors, and operators.`,
    image: {
      src: blinkLaunchSlide02,
      altText: "Engineer using Blink"
    }
  }, {
    title: "Make better decisions, faster",
    description: <>
          Bring your Blink data to life here in Sphere XG — where you can
          quickly view, manage, analyze, and share your scan data, all in one
          place.
          <br />
          <br />
          Learn more about{" "}
          <ExternalLinks text="Blink" link="blinkLaunch" trackEventProps={{
        topic: SplashScreenSdbTopic.blinkLaunch,
        link: "blinkLaunch"
      }} />
        </>,
    image: {
      src: blinkLaunchSlide03,
      altText: "Working with Blink data in Sphere XG"
    }
  }]
};
const floorPlanGenerationScreens = {
  topic: SplashScreenSdbTopic.floorPlanGeneration,
  // All users should see this splash screen
  allowedRoles: FaroCoreApiConst.allCompanyRoles,
  // Show the splash screen only if the company has the "floorplan-generator" feature
  requiredFeatures: ["floorplan-generator"],
  // Start displaying splash screen in production on the 28th of August 2025, at 15:00 CET
  startDate: "2025-08-28T13:00:00Z",
  content: [{
    title: "New: Floor Plan Generation",
    description: "Transform complex 3D scan data into clear, actionable 2D layouts that enhance presentations, marketing materials, and project planning across industries.",
    image: {
      src: floorPlanGenerationSlide01,
      altText: "Floor plan generation"
    }
  }, {
    title: "Turn 3D Scans Into 2D Floor Plans!",
    description: <>
          The simplified floor plans provide an overview of room layouts and
          structural elements. Perfect for positioning 360° photos and
          understanding Orbis or VideoMode walking paths within Sphere XG.
          <br />
          <br />
          <ExternalLinks text="Learn more" link="floorPlanGeneration" trackEventProps={{
        topic: SplashScreenSdbTopic.floorPlanGeneration,
        link: "floorPlanGeneration"
      }} />
        </>,
    image: {
      src: floorPlanGenerationSlide02,
      altText: "Floor plan with waypoints"
    }
  }]
};
const accProcoreIntegrationScreens = {
  topic: SplashScreenSdbTopic.accProcoreIntegration,
  // All users should see this splash screen
  allowedRoles: FaroCoreApiConst.allCompanyRoles,
  content: [{
    title: <>
          Boost Jobsite Productivity with ACC and Procore<sup>®</sup>{" "}
          Integrations in FARO Sphere<sup>®</sup> XG
        </>,
    description: <>
          Enrich your Autodesk Construction Cloud<sup>®</sup> Issues and Procore
          Observations with reality capture data to experience seamless
          collaboration and data consistency across entire project lifecycles.
          Simply create annotations through Sphere XG and sync them across
          platforms in one click.
        </>,
    image: {
      src: accProcoreIntegrationSlide01,
      altText: "ACC and Procore Integrations"
    }
  }, {
    title: "Richer Context for Issues and Observations: Real-Time Data Sync Across Platforms",
    description: <>
          View and verify project data in real-time on either platform. Two-way
          sync between Sphere XG and Procore/ ACC platforms keeps team members,
          partners, and stakeholders updated. Provide teams with accurate
          information regardless of whether they access Sphere XG, ACC, or the
          Procore platform.
          <br />
          <br />
          <ExternalLinks text="Learn more" link="accProcoreIntegration" trackEventProps={{
        topic: SplashScreenSdbTopic.accProcoreIntegration,
        link: "accProcoreIntegration"
      }} />
        </>,
    image: {
      src: accProcoreIntegrationSlide02,
      altText: "Data Sync across Platforms"
    }
  }]
};
const droneDataScreens = {
  topic: SplashScreenSdbTopic.droneData,
  // All users should see this splash screen regardless of their role
  allowedRoles: FaroCoreApiConst.allCompanyRoles,
  // Show the splash screen only if the company has pointcloud access
  requiredFeatures: ["global-pointcloud-read"],
  // Blocking translation
  shouldBlockTranslation: true,
  content: [{
    title: <>
          Turn drone images into point clouds in the cloud - now in FARO Sphere
          <sup>®</sup> XG (Beta)
        </>,
    description: <>
          We’re excited to announce a new beta feature in Sphere XG: Drone
          Imagery Photogrammetry. With this early-access capability, you can
          process drone image sets directly in Sphere XG and generate accurate
          point clouds; fully cloud-native, with no GPUs, plugins, or external
          software required.
        </>,
    image: {
      src: droneDataSlide01,
      altText: "Turning drone images into point clouds"
    }
  }, {
    title: "Seamless integration with terrestrial and mobile data",
    description: <>
          This release strengthens connectivity and brings drone, terrestrial
          and mobile data together in one unified environment, reducing field
          time, lowering costs, and accelerating your path from capture to
          deliverable.
          <br />
          Ideal for progress tracking, communication and early-stage analysis
          (e.g. volumetric analysis).
        </>,
    image: {
      src: droneDataSlide02,
      altText: "Turning drone images into point clouds"
    }
  }, {
    title: "Built for fast results today with more to come",
    description: <>
          This unified cloud workflow simplifies tools, accelerates processing,
          and helps teams turn drone data into valuable insights faster than
          ever.
          <br />
          <br />
          <b>
            *As a beta release, this feature is designed for early access and
            will continue to evolve, shaped by real customer feedback and
            ongoing improvements.
          </b>
          <br />
          <br />
          <ExternalLinks text="Learn more" link="droneData" trackEventProps={{
        topic: SplashScreenSdbTopic.droneData,
        link: "droneData"
      }} />
        </>,
    image: {
      src: droneDataSlide03,
      altText: "Drone data processing in Sphere XG"
    }
  }]
};
const hbToSxGMigratedScreens = {
  topic: SplashScreenSdbTopic.hbToSxgMigrated,
  allowedRoles: FaroCoreApiConst.allCompanyRoles,
  // Display only to users of companies with the "hb-to-sxg-migrated" feature enabled
  requiredFeatures: ["hb-to-sxg-migrated"],
  // This splash screen component is already imported from flat-ui and rendered directly
  // in the SplashScreen component. Therefore, there is no need to provide content here.
  content: []
};
export const splashScreenList = {
  [SplashScreenSdbTopic.welcomeToFaro]: welcomeToFaroScreens,
  [SplashScreenSdbTopic.import3DModels]: import3DModelsScreens,
  [SplashScreenSdbTopic.blinkLaunch]: blinkLaunchScreens,
  [SplashScreenSdbTopic.floorPlanGeneration]: floorPlanGenerationScreens,
  [SplashScreenSdbTopic.accProcoreIntegration]: accProcoreIntegrationScreens,
  [SplashScreenSdbTopic.droneData]: droneDataScreens,
  [SplashScreenSdbTopic.hbToSxgMigrated]: hbToSxGMigratedScreens
};